import React, { useCallback, useMemo } from "react";
import { Link, matchRoutes, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import modules from "../../constants/modules";
import { IModule } from "./types";
import { AppSwitcher } from "./components/app-switcher";
import { Breadcrumb } from "./components/breadcrumb";
import { GlobalSearch } from "./components/global-search";
import { LanguageSwitch } from "./components/language-switch";
import { NotificationMenu } from "./components/notification-menu";
import { UserMenu } from "./components/user-menu";
import { useAuth } from '@erpsquad/common/runtime';
import { getApiConfigSafe } from "../../utils/api-config";
import { images } from "../../assets/images";
import { Pathname } from "../../constants/pathnames/pathname";
import { breadcrumbSegmentLabelMap } from "../../constants/route-page-map";
import "./header.scss";

export function Header(props: { isDropdownShow?: boolean }): React.ReactElement {
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  const { user } = useAuth();
  const appLogo = getApiConfigSafe()?.appLogo || images.logo;

  const checkMatchedRoutes = useCallback(
    (paths: string[] | string) => {
      const shouldMatch: { path: string }[] = Array.isArray(paths)
        ? paths.map((path: string) => ({ path }))
        : [{ path: paths }];
      return Boolean(matchRoutes(shouldMatch, location));
    },
    [location],
  );

  const erp_modules = useMemo<IModule[]>(() => {
    const allModules = modules(t);

    const u_data = localStorage.getItem("_u_data");
    const userData = u_data ? JSON.parse(u_data) : null;
    const accessModules: { module_name: string }[] =
      userData?.module_access?.length > 0
        ? userData.module_access
        : [{ module_name: "user" }];

    // Stage 1: filter by module_access
    const stage1 = allModules.filter((m) =>
      m.beModuleName === "rbac" ||
      accessModules.some((a: { module_name: string }) => a.module_name === m.beModuleName)
    );

    // Stage 2: filter by role-permission.modules
    const rolePermission = localStorage.getItem("role-permission");
    const permissions: string[] =
      rolePermission && JSON.parse(rolePermission)?.modules
        ? JSON.parse(rolePermission).modules
        : [];

    if (!permissions.length) return stage1;
    return stage1.filter((m) => permissions.includes(m.beModuleName));
  }, [t]);

  const selectedModule = useMemo<IModule | null>(() => {
    const byRoute = erp_modules.find(
      (module: IModule) =>
        module?.link?.toLowerCase() === pathname.toLowerCase() ||
        checkMatchedRoutes(module.connectedRoutes),
    );
    if (byRoute) return byRoute;
    return erp_modules.find(
      (module: IModule) =>
        module.link.length > 1 &&
        pathname.toLowerCase().startsWith(module.link.toLowerCase() + "/"),
    ) || null;
  }, [erp_modules, pathname, checkMatchedRoutes]);

  const breadcrumbSegments = useMemo(() => {
    if (!selectedModule) return [];
    const base = selectedModule.link;
    const rest = pathname.toLowerCase().startsWith(base.toLowerCase())
      ? pathname.slice(base.length)
      : pathname;
    const rawSegments = rest.split("/").filter(s => s);
    const routeObjects = selectedModule.connectedRoutes.map(r => ({ path: r }));
    const result: { label: string; path: string; navigable: boolean }[] = [];
    for (let i = 0; i < rawSegments.length; i++) {
      const seg = rawSegments[i];
      if (/^\d+$/.test(seg)) continue;
      const path = base + "/" + rawSegments.slice(0, i + 1).join("/");
      const label = breadcrumbSegmentLabelMap[seg] || seg.split("-").map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
      const navigable = Boolean(matchRoutes(routeObjects, { pathname: path }));
      result.push({ label, path, navigable });
    }
    return result.filter(s => Boolean(s && s.label));
  }, [pathname, selectedModule]);

  return (
    <div className="app-bar">
      {pathname === Pathname.DASHBOARD && (
        <Link style={{ marginTop: "0.25rem" }} to={user ? Pathname.DASHBOARD : Pathname.LOGIN}>
          <img src={appLogo} alt="logo" height={55} />
        </Link>
      )}
      {pathname !== Pathname.DASHBOARD && (
        <>
          <AppSwitcher erp_modules={erp_modules} selectedModule={selectedModule} />
          <div className="ab-divider" />
        </>
      )}
      <Breadcrumb selectedModule={selectedModule} segments={breadcrumbSegments} />
      <div style={{ flex: 1 }} />
      <GlobalSearch />
      <div className="notif-wrap">
        <NotificationMenu />
      </div>
      <LanguageSwitch />
      <div className="v-divider" />
      <UserMenu />
    </div>
  );
}

export default Header;
